import browser from '@openclassrooms/utils/browser';

let isAceLoading = false;
const aceLoadingPromisesResolvers: Array<(ace: any) => void> = [];

const ACE_VERSION = '1.13.0';
const BASE_PATH = `https://cdnjs.cloudflare.com/ajax/libs/ace/${ACE_VERSION}`;
const ACE_PATH = `${BASE_PATH}/ace.js`;
const HIGHLIGHT_PATH = `${BASE_PATH}/ext-static_highlight.js`;

function isAceReady() {
  return Boolean((window as any).ace);
}

function getInstance() {
  if (isAceReady()) {
    return Promise.resolve((window as any).ace);
  }
  return new Promise<any>((resolve) => {
    aceLoadingPromisesResolvers.push(resolve);
    if (isAceLoading) return;
    isAceLoading = true;

    if (!browser.window.ace) {
      browser.loadScript({
        src: ACE_PATH,
        onLoaded: () => {
          browser.loadScript({
            src: HIGHLIGHT_PATH,
            onLoaded: () => {
              const { ace } = browser.window;

              ace.config.set('basePath', BASE_PATH);
              ace.config.set('modePath', BASE_PATH);
              ace.config.set('themePath', BASE_PATH);
              ace.config.set('workerPath', BASE_PATH);

              aceLoadingPromisesResolvers.forEach((resolver) => resolver(ace));
              isAceLoading = false;
            },
          });
        },
      });
    }
  });
}

function syntaxHighlight(domElement: HTMLElement, language: string, showGutter: boolean) {
  return getInstance().then((ace) => {
    const highlight = ace.require('ace/ext/static_highlight');
    highlight.highlight(domElement, {
      mode: `ace/mode/${language}`,
      theme: 'ace/theme/dracula',
      startLineNumber: 1,
      showGutter,
      trim: true,
    });

    const createCopyIcon = (className: string, svgString: string) => {
      const existingElement = document.querySelector(`.${className}`) as HTMLElement | null;
      if (existingElement) return existingElement;

      const element = document.createElement('button');
      element.className = className;
      element.innerHTML = svgString;
      Object.assign(element.style, {
        position: 'absolute',
        right: '8px',
        top: '8px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        border: 'none',
        display: 'inline-block',
        lineHeight: '1',
        padding: '0px',
      } as Partial<CSSStyleDeclaration>);
      return element;
    };

    const isOnQuizPage = window.location.href.includes('exercises');
    const isOnCoursePage = window.location.href.includes('courses');
    const isOnProjectPage = window.location.href.includes('projects');
    const isOnIOS = !isOnQuizPage && !isOnCoursePage && !isOnProjectPage;

    if (!isOnQuizPage && !isOnIOS) {
      domElement.addEventListener('mouseenter', () => {
        const copyTextElement = createCopyIcon(
          'oc-copy-text',
          '<svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 18 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path fill="#ffffff" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>'
        );

        copyTextElement.addEventListener('click', () => {
          const HIDE_ICON_TIMEOUT = 2000;
          const code = (domElement.textContent || '').trim();
          navigator.clipboard.writeText(code).then(() => {
            (copyTextElement.style as any).display = 'none';

            const copiedTextElement = createCopyIcon(
              'oc-copied-text',
              '<svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 18 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path fill="#ffffff" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>'
            );

            domElement.appendChild(copiedTextElement);
            setTimeout(() => {
              (copiedTextElement.style as any).display = 'none';
            }, HIDE_ICON_TIMEOUT);
          });
        });

        domElement.appendChild(copyTextElement);
      });

      domElement.addEventListener('mouseleave', () => {
        const copyTextElement = domElement.querySelector('.oc-copy-text');
        const copiedTextElement = domElement.querySelector('.oc-copied-text');
        copyTextElement?.remove();
        copiedTextElement?.remove();
      });
    }
  });
}

export default { isAceReady, getInstance, syntaxHighlight };
